import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { fmtDateShort } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';
import { useActionStreamStore } from '../../stores/actionStream.store';
import { useNotifications } from '../../hooks/useNotifications';
import {
  computeUpcoming, toUnifiedRows, invoicesQueryOptions, quotesQueryOptions,
  type QuoteItem, type UnifiedRow,
} from '../../lib/scadenziario';
import type { IconName } from '../../components/ui/Icon';
import { tasksSummaryQuery, daysToDue, contextLine, type TaskSummaryItem } from '../tasks/tasksApi';
import {
  adminOverviewQuery, upcomingRateQuery, daysFromToday, TAX_SOURCE_LABEL,
  type IncassoRow, type TaxNext, type ToReview,
} from '../admin/adminApi';
import { fmtDate } from '../../lib/utils';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';

/**
 * useActionStream — composer dello stream "Oggi".
 *
 * Aggrega in un'unica lista azionabile le fonti già esistenti (cache TanStack
 * condivisa, nessun fetch nuovo):
 *   · ['invoices'] + ['quotes']        → scadenziario (incassi e firme)
 *   · ['notifications']                → alert di progetto + FYI personali
 *   · ['dashboard', q, y]              → contatori aggregati (bozze, candidati)
 *   · ['tasks','summary','mine']       → i miei task in ritardo e in scadenza oggi
 *   · ['admin','overview'] + rate      → Amministrazione: rate scadute / in scadenza,
 *                                        scadenze fiscali entro 7 giorni, "da controllare"
 *
 * Bande di urgenza:
 *   0 = Critico (rate/fatture scadute, tasse entro 7 gg, alert rischio/margine, task in ritardo)
 *   1 = Oggi    (scadenze entro domani, da controllare, preventivi in scadenza, task che scadono oggi)
 *   2 = Settimana (scadenze ≤ 7gg, preventivi in attesa, item aggregati)
 *   3 = FYI     (notifiche personali non lette)
 *
 * Con il modulo AMMINISTRAZIONE le rate (fonte FiC, anche multiple per fattura)
 * sostituiscono le fatture dello scadenziario di Contabilità: stessa cosa,
 * più precisa. Senza il modulo resta il comportamento di prima.
 *
 * Hook puro-composer: NESSUNA mutation viene eseguita qui — `primary` e
 * `actionData` descrivono l'azione, il componente riga la collega.
 */

export type ActionBand = 0 | 1 | 2 | 3;
export type ActionKind =
  | 'invoice-overdue' | 'invoice-due' | 'quote-pending'
  | 'project-alert' | 'candidate-ready' | 'draft-project' | 'notification'
  | 'task-overdue' | 'task-due'
  | 'rata-overdue' | 'rata-due' | 'tax-due' | 'to-review';
export type ActionPrimaryKind =
  | 'sollecita' | 'incassa' | 'reinvia' | 'risolvi' | 'apri' | 'copia-link' | 'completa'
  | 'incassa-rata' | 'conferma-incasso';
// 'notification' è il fallback per le notifiche con link non riconducibile a un'entità
export type ActionEntityType = 'invoice' | 'quote' | 'project' | 'client' | 'candidate' | 'notification' | 'task' | 'rata' | 'tax';

export interface ActionItem {
  /** `entityType:id` — chiave stabile per snooze / archivio / dedupe. */
  key: string;
  band: ActionBand;
  kind: ActionKind;
  icon: IconName;
  title: string;
  sub: string;
  amount?: number;
  dueAt?: string | null;
  entity: { type: ActionEntityType; id: string; path: string };
  primary?: { label: string; kind: ActionPrimaryKind };
  canSnooze: boolean;
  canDone: boolean;
  /** Payload minimo per il wiring delle mutation nel componente riga (fase UI). */
  actionData?: {
    clientId?: string;
    number?: string | null;
    title?: string | null;
    alertId?: string;
    notifId?: string;
    /** Stato del task prima di "Completa" (per l'Annulla del toast). */
    taskStatus?: string;
    /** Rata (Amministrazione): id del pagamento e, se c'è, il movimento Qonto compatibile. */
    paymentId?: string;
    transactionId?: string;
    settledAt?: string;
  };
}

export interface ActionStreamCounts {
  critical: number;
  today: number;
  week: number;
  later: number;
  snoozed: number;
}

export interface ActionBandGroup { band: ActionBand; label: string; items: ActionItem[] }

export const BAND_LABEL: Record<ActionBand, string> = {
  0: 'Critico',
  1: 'Oggi',
  2: 'Questa settimana',
  3: 'FYI',
};

const DAY_MS = 86400000;

// Sottoinsieme di OverviewPayload (DashboardPage): allo stream servono solo i contatori.
interface OverviewLite { kpis: { draftProjects: number; pipelineCandidates: number } }

// Icona notifica server → IconName (stessa semantica dell'ICON_MAP in Topbar).
const NOTIF_ICON: Record<string, IconName> = {
  INVOICE: 'invoice', CALENDAR: 'calendar', TALENT: 'talent', FILE: 'file', ALERT: 'alert', OTHER: 'bell',
};

// Deriva l'entità da un link di notifica noto: così una notifica sulla
// fattura X si deduplica con l'item fattura X (stessa chiave entità).
const LINK_PATTERNS: Array<{ re: RegExp; type: ActionEntityType }> = [
  { re: /^\/tasks\/([^/?#]+)/, type: 'task' },
  { re: /^\/invoices\/([^/?#]+)/, type: 'invoice' },
  { re: /^\/quotes\/([^/?#]+)/, type: 'quote' },
  { re: /^\/projects\/([^/?#]+)/, type: 'project' },
  { re: /^\/clients\/([^/?#]+)/, type: 'client' },
];
function entityFromLink(link: string | null): ActionItem['entity'] | null {
  if (!link) return null;
  for (const p of LINK_PATTERNS) {
    const m = p.re.exec(link);
    if (m?.[1]) return { type: p.type, id: m[1], path: link };
  }
  return null;
}

/** Fattura in ritardo → band 0. Copre sia EMESSA/INVIATA oltre scadenza sia status SCADUTA. */
function invoiceOverdueItem(r: UnifiedRow, due: number | null, now: number): ActionItem {
  // Stessa convenzione dello Scadenziario: days = ceil((due - now) / giorno), ritardo = -days
  const late = due != null ? -Math.ceil((due - now) / DAY_MS) : null;
  return {
    key: `invoice:${r.id}`,
    band: 0,
    kind: 'invoice-overdue',
    icon: 'alert',
    title: `Fattura ${r.number ?? 's/n'} · ${r.client.legalName}`,
    sub: late == null ? 'Scaduta — da sollecitare' : late <= 0 ? 'Scaduta oggi' : `Scaduta da ${late} g`,
    amount: Number(r.total),
    dueAt: r.secondaryDate,
    entity: { type: 'invoice', id: r.id, path: `/invoices/${r.id}` },
    primary: { label: 'Sollecita', kind: 'sollecita' },
    canSnooze: true, // band 0: lo store clampa lo snooze a domani 09:00
    canDone: true,   // done = Incassa (chiude l'item)
    actionData: { clientId: r.client.id, number: r.number },
  };
}

/** Fattura aperta in scadenza → band 1 (entro domani) o 2 (entro 7 giorni). */
function invoiceDueItem(r: UnifiedRow, due: number, now: number, band: 1 | 2): ActionItem {
  const days = Math.ceil((due - now) / DAY_MS);
  return {
    key: `invoice:${r.id}`,
    band,
    kind: 'invoice-due',
    icon: 'invoice',
    title: `Fattura ${r.number ?? 's/n'} · ${r.client.legalName}`,
    sub: days <= 0 ? 'Scade oggi' : days === 1 ? 'Scade domani' : `Scade tra ${days} g`,
    amount: Number(r.total),
    dueAt: r.secondaryDate,
    entity: { type: 'invoice', id: r.id, path: `/invoices/${r.id}` },
    primary: { label: 'Incassa', kind: 'incassa' },
    canSnooze: true,
    canDone: true,
    actionData: { clientId: r.client.id, number: r.number },
  };
}

/** Preventivo INVIATO/VISUALIZZATO in attesa di firma → band 1 se la scadenza è vicina, altrimenti 2. */
function quotePendingItem(r: UnifiedRow, band: 1 | 2, due: number | null, now: number): ActionItem {
  const quote = r.raw as QuoteItem;
  const days = due != null ? Math.ceil((due - now) / DAY_MS) : null;
  const expiry = days == null ? null
    : days < 0 ? `validità superata da ${-days} g`
    : days === 0 ? 'scade oggi'
    : `scade tra ${days} g`;
  return {
    key: `quote:${r.id}`,
    band,
    kind: 'quote-pending',
    icon: 'send',
    title: `Preventivo ${r.number ?? 's/n'} · ${r.client.legalName}`,
    sub: `${r.status === 'VISUALIZZATO' ? 'Visto dal cliente' : 'Inviato'}, in attesa di firma${expiry ? ` · ${expiry}` : ''}`,
    amount: Number(r.total),
    dueAt: r.secondaryDate,
    entity: { type: 'quote', id: r.id, path: `/quotes/${r.id}` },
    primary: { label: 'Reinvia', kind: 'reinvia' },
    canSnooze: true,
    canDone: false, // la firma dipende dal cliente: non si "chiude" da qui
    actionData: { clientId: r.client.id, number: r.number, title: quote.title },
  };
}

/** Task mio in ritardo (band 0) o che scade oggi (band 1): primary = Completa. */
function taskActionItem(t: TaskSummaryItem, band: 0 | 1): ActionItem {
  const days = daysToDue(t.dueDate);
  const ctx = contextLine(t);
  const when = band === 0
    ? (days != null && days < 0 ? `In ritardo di ${-days} g` : 'In ritardo')
    : 'Scade oggi';
  return {
    key: `task:${t.id}`,
    band,
    kind: band === 0 ? 'task-overdue' : 'task-due',
    icon: 'tasks',
    title: t.title,
    sub: ctx ? `${when} · ${ctx}` : when,
    dueAt: t.dueDate,
    entity: { type: 'task', id: t.id, path: `/tasks/${t.id}` },
    primary: { label: 'Completa', kind: 'completa' },
    canSnooze: true,
    canDone: true, // done = Completa (chiude il task)
    actionData: { title: t.title, taskStatus: t.status },
  };
}

/**
 * Rata di una fattura emessa (Amministrazione). Scaduta → band 0 (Incassa);
 * entro domani → 1; entro 7 giorni → 2. Se esiste un bonifico Qonto
 * compatibile, la primary diventa "Conferma" con quel movimento.
 */
function rataItem(r: IncassoRow, band: 0 | 1 | 2, review: ToReview | undefined): ActionItem {
  const days = daysFromToday(r.dueDate);
  const rata = r.ofTotal > 1 ? ` · rata ${r.seq}/${r.ofTotal}` : '';
  const when = days == null ? 'Senza scadenza'
    : days < 0 ? `Scaduta da ${-days} g`
    : days === 0 ? 'Scade oggi'
    : days === 1 ? 'Scade domani'
    : `Scade tra ${days} g`;
  const hint = review ? ` · bonifico del ${fmtDate(review.transaction.settledOn)} in banca` : '';
  return {
    key: `rata:${r.id}`,
    band,
    kind: band === 0 ? 'rata-overdue' : 'rata-due',
    icon: band === 0 ? 'alert' : 'coins',
    title: `Fattura ${r.invoiceNumber} · ${r.client.shortName}`,
    sub: `${when}${rata}${hint}`,
    amount: r.amount,
    dueAt: r.dueDate,
    entity: { type: 'rata', id: r.id, path: `/admin?tab=incassi&q=${encodeURIComponent(r.invoiceNumber)}` },
    primary: review ? { label: 'Conferma', kind: 'conferma-incasso' } : { label: 'Incassa', kind: 'incassa-rata' },
    canSnooze: true,
    canDone: true, // done = Incassa (chiude la rata)
    actionData: { paymentId: r.id, number: r.invoiceNumber, transactionId: review?.transaction.id, settledAt: review?.transaction.settledOn },
  };
}

/** Rata con un movimento Qonto compatibile ma non in scadenza a breve → Oggi, "Conferma". */
function toReviewItem(t: ToReview): ActionItem {
  return {
    key: `rata:${t.paymentId}`,
    band: 1,
    kind: 'to-review',
    icon: 'swap',
    title: `Fattura ${t.invoiceNumber} · ${t.clientName}`,
    sub: `Bonifico del ${fmtDate(t.transaction.settledOn)} in banca: è questo incasso?`,
    amount: t.amount,
    dueAt: t.dueDate,
    entity: { type: 'rata', id: t.paymentId, path: `/admin?tab=incassi&q=${encodeURIComponent(t.invoiceNumber)}` },
    primary: { label: 'Conferma', kind: 'conferma-incasso' },
    canSnooze: true,
    canDone: true,
    actionData: { paymentId: t.paymentId, number: t.invoiceNumber, transactionId: t.transaction.id, settledAt: t.transaction.settledOn },
  };
}

/** Scadenza fiscale entro 7 giorni (o già passata) → Critico. Si apre, non si "chiude" da qui. */
function taxItem(t: TaxNext, idx: number): ActionItem {
  const days = daysFromToday(t.dueDate);
  const when = days == null ? '' : days < 0 ? `Scaduta da ${-days} g` : days === 0 ? 'Scade oggi' : days === 1 ? 'Scade domani' : `Scade tra ${days} g`;
  const src = t.source === 'STIMA' ? ` · ${TAX_SOURCE_LABEL.STIMA.toLowerCase()}, da confermare` : '';
  const id = t.installmentId ?? t.obligationId ?? `${t.kind}-${t.dueDate}-${idx}`;
  return {
    key: `tax:${id}`,
    band: 0,
    kind: 'tax-due',
    icon: 'calendarClock',
    title: t.description,
    sub: `${when}${src}`,
    amount: t.amount,
    dueAt: t.dueDate,
    entity: { type: 'tax', id, path: '/admin?tab=fisco' },
    primary: { label: 'Apri', kind: 'apri' },
    canSnooze: true,
    canDone: false,
  };
}

/**
 * Ordinamento di severità DENTRO la banda (le bande sono già ordinate asc):
 *   band 0 → alert di progetto prima, poi fatture per giorni-ritardo desc
 *            (dueAt più vecchia prima) e importo desc;
 *   band 1-2 → scadenza più vicina prima (senza data in coda), poi importo desc;
 *   band 3 → ordine d'arrivo (l'API consegna già le notifiche createdAt desc).
 */
function compareItems(a: ActionItem, b: ActionItem): number {
  if (a.band !== b.band) return a.band - b.band;
  if (a.band === 3) return 0; // sort stabile: preserva l'ordine della fonte
  if (a.band === 0) {
    const ka = a.kind === 'project-alert' ? 0 : 1;
    const kb = b.kind === 'project-alert' ? 0 : 1;
    if (ka !== kb) return ka - kb;
  }
  const da = a.dueAt ? new Date(a.dueAt).getTime() : Infinity;
  const db = b.dueAt ? new Date(b.dueAt).getTime() : Infinity;
  if (da !== db) return da - db;
  return (b.amount ?? 0) - (a.amount ?? 0);
}

export function useActionStream(): {
  items: ActionItem[];
  counts: ActionStreamCounts;
  bands: ActionBandGroup[];
  isLoading: boolean;
} {
  const user = useAuthStore((s) => s.user);
  const snoozedMap = useActionStreamStore((s) => s.snoozed);
  const archivedFyi = useActionStreamStore((s) => s.archivedFyi);

  // Gating per modulo: stesso pattern della Sidebar (hasAtLeast READ).
  const canSee = (module: ModuleKey) => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === module)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.READ);
  };
  const canFatturazione = canSee(ModuleKey.FATTURAZIONE);
  const canDashboard = canSee(ModuleKey.DASHBOARD);
  const canTalent = canSee(ModuleKey.TALENT);
  const canTasks = canSee(ModuleKey.TASKS);
  const canAdmin = canSee(ModuleKey.AMMINISTRAZIONE);
  const canSeeMargin = !!user?.role.canSeeMargin;

  // Fonti dati — stesse queryKey delle viste esistenti: cache condivisa, zero fetch extra.
  const { data: notifData, isLoading: loadingNotif } = useNotifications();
  const { data: invoiceData, isLoading: loadingInv } = useQuery({ ...invoicesQueryOptions, enabled: canFatturazione });
  const { data: quoteData, isLoading: loadingQuotes } = useQuery({ ...quotesQueryOptions, enabled: canFatturazione });

  // Trimestre corrente calcolato come in DashboardPage → stessa queryKey, stesso fetch.
  const today = new Date();
  const currentQ = Math.floor(today.getMonth() / 3) + 1;
  const currentY = today.getFullYear();
  const { data: overview, isLoading: loadingDash } = useQuery({
    queryKey: ['dashboard', currentQ, currentY],
    queryFn: () => api.get<OverviewLite>(`/dashboard/overview?q=${currentQ}&y=${currentY}`),
    enabled: canDashboard,
  });

  // Task: il riepilogo "mine" (stessa query del badge Sidebar); staleTime e
  // refetchInterval vivono nelle query options → nessun fetch a raffica.
  const { data: taskSummary, isLoading: loadingTasks } = useQuery({ ...tasksSummaryQuery('mine'), enabled: canTasks });

  // Amministrazione: overview (tasse, da controllare) + rate aperte a 30 giorni
  // (stesse query della pagina Oggi e di /admin: cache condivisa). Se l'API
  // non risponde, lo stream vive lo stesso con le altre fonti.
  const { data: adminOverview, isLoading: loadingAdmin } = useQuery({ ...adminOverviewQuery, enabled: canAdmin, retry: 1 });
  const { data: rateData, isLoading: loadingRate } = useQuery({ ...upcomingRateQuery(), enabled: canAdmin, retry: 1 });

  const isLoading = loadingNotif
    || (canFatturazione && (loadingInv || loadingQuotes))
    || (canDashboard && loadingDash)
    || (canTasks && loadingTasks)
    || (canAdmin && (loadingAdmin || loadingRate));

  const composed = useMemo(() => {
    const now = Date.now();
    // Fine di domani = tetto della banda 1 ("entro domani")
    const tomorrowEnd = new Date();
    tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);
    tomorrowEnd.setHours(23, 59, 59, 999);
    const band1Cutoff = tomorrowEnd.getTime();
    const weekCutoff = now + 7 * DAY_MS;

    const collected: ActionItem[] = [];

    // ── Amministrazione: rate (sostituiscono le fatture dello scadenziario), tasse, da controllare ──
    const useAdminRates = canAdmin && !!rateData;
    if (canAdmin) {
      const reviewByPayment = new Map<string, ToReview>();
      for (const t of adminOverview?.fatture.toReview ?? []) reviewByPayment.set(t.paymentId, t);
      const seenRate = new Set<string>();
      for (const r of rateData?.items ?? []) {
        if (r.status !== 'DA_INCASSARE') continue;
        const days = daysFromToday(r.dueDate);
        if (days == null) continue;
        const band: 0 | 1 | 2 | null = days < 0 ? 0 : days <= 1 ? 1 : days <= 7 ? 2 : null;
        if (band == null) continue; // oltre 7 giorni: vive nella card "Prossime scadenze", non nello stream
        seenRate.add(r.id);
        collected.push(rataItem(r, band, reviewByPayment.get(r.id)));
      }
      for (const t of reviewByPayment.values()) {
        if (!seenRate.has(t.paymentId)) collected.push(toReviewItem(t));
      }
      (adminOverview?.tasse.next ?? []).forEach((t, i) => {
        const days = daysFromToday(t.dueDate);
        if (days != null && days <= 7) collected.push(taxItem(t, i));
      });
    }

    // ── Contabilità: scadenziario → fatture e preventivi azionabili ──
    if (canFatturazione && invoiceData && quoteData) {
      const rows = toUnifiedRows(quoteData.items ?? [], invoiceData.items ?? []);
      for (const r of computeUpcoming(rows)) {
        const due = r.secondaryDate ? new Date(r.secondaryDate).getTime() : null;
        if (r.kind === 'invoice') {
          if (useAdminRates) continue; // le rate dell'Amministrazione coprono già queste fatture
          if (due != null && due < now) collected.push(invoiceOverdueItem(r, due, now));
          else if (due != null && due <= band1Cutoff) collected.push(invoiceDueItem(r, due, now, 1));
          else if (due != null && due <= weekCutoff) collected.push(invoiceDueItem(r, due, now, 2));
          // oltre 7 giorni (o senza scadenza): fuori dallo stream "Oggi", vive nello Scadenziario
        } else {
          // preventivi INVIATO/VISUALIZZATO (già filtrati da computeUpcoming)
          const near = due != null && due <= weekCutoff;
          collected.push(quotePendingItem(r, near ? 1 : 2, due, now));
        }
      }
      // Fatture già marcate SCADUTA: escluse da computeUpcoming (che considera
      // solo EMESSA/INVIATA) ma sono comunque incassi in ritardo → banda 0.
      for (const r of rows) {
        if (useAdminRates) break;
        if (r.kind === 'invoice' && r.status === 'SCADUTA') {
          const due = r.secondaryDate ? new Date(r.secondaryDate).getTime() : null;
          collected.push(invoiceOverdueItem(r, due, now));
        }
      }
    }

    // ── Task miei: in ritardo → Critico, scadono oggi → Oggi (link al dettaglio) ──
    if (canTasks && taskSummary) {
      for (const t of taskSummary.overdueItems ?? []) collected.push(taskActionItem(t, 0));
      for (const t of taskSummary.dueTodayItems ?? []) collected.push(taskActionItem(t, 1));
    }

    // ── Alert di progetto (fonte: ['notifications'], come campanella e Dashboard).
    // Dedup con gli alert dell'overview: sono gli STESSI record ProjectAlert,
    // quindi si consuma solo questa fonte — nessun doppione possibile.
    for (const a of notifData?.projectAlerts ?? []) {
      const marginRelated = /margin/i.test(a.message); // copre "margine" e "margin"
      if (marginRelated && !canSeeMargin) continue;    // gating margine per ruolo
      const critical = a.type === 'ALERT' || marginRelated || /risk|rischio/i.test(a.message);
      collected.push({
        key: a.project?.id ? `project:${a.project.id}` : `project-alert:${a.id}`,
        band: critical ? 0 : 1,
        kind: 'project-alert',
        icon: 'alert',
        title: a.message,
        sub: a.project ? `${a.project.code} · ${a.project.name}` : 'Alert di progetto',
        dueAt: null,
        entity: a.project?.id
          ? { type: 'project', id: a.project.id, path: `/projects/${a.project.id}` }
          : { type: 'project', id: a.id, path: '/' },
        primary: { label: 'Risolvi', kind: 'risolvi' },
        canSnooze: true,
        canDone: true, // done = Risolvi (chiude l'alert per tutti)
        actionData: { alertId: a.id },
      });
    }

    // ── Item aggregati dall'overview (derivati: si aprono, non si "completano") ──
    const draftCount = canDashboard ? overview?.kpis.draftProjects ?? 0 : 0;
    if (draftCount > 0 && canFatturazione) {
      collected.push({
        key: 'project:drafts',
        band: 2,
        kind: 'draft-project',
        icon: 'file',
        title: `${draftCount} progett${draftCount === 1 ? 'o' : 'i'} in preventivo`,
        sub: 'Bozze da trasformare in proposta firmabile',
        dueAt: null,
        entity: { type: 'project', id: 'drafts', path: '/admin?tab=documenti&type=quote' },
        primary: { label: 'Apri', kind: 'apri' },
        canSnooze: true,
        canDone: false,
      });
    }
    const candCount = canDashboard ? overview?.kpis.pipelineCandidates ?? 0 : 0;
    if (candCount > 0 && canTalent) {
      collected.push({
        key: 'candidate:pipeline',
        band: 2,
        kind: 'candidate-ready',
        icon: 'talent',
        title: `${candCount} candidat${candCount === 1 ? 'o' : 'i'} in pipeline`,
        sub: 'Pronti per screening o placement',
        dueAt: null,
        entity: { type: 'candidate', id: 'pipeline', path: '/talent' },
        primary: { label: 'Apri', kind: 'apri' },
        canSnooze: true,
        canDone: false,
      });
    }

    // ── Notifiche personali non lette → FYI (band 3) ──
    for (const n of notifData?.personal ?? []) {
      if (n.readAt) continue; // il letto è già "gestito"
      const ent = entityFromLink(n.link);
      collected.push({
        key: ent ? `${ent.type}:${ent.id}` : `notification:${n.id}`,
        band: 3,
        kind: 'notification',
        icon: NOTIF_ICON[n.icon] ?? 'bell',
        title: n.message,
        sub: `FYI · ${fmtDateShort(n.createdAt)}`,
        dueAt: null,
        entity: ent ?? { type: 'notification', id: n.id, path: n.link ?? '/' },
        primary: n.link ? { label: 'Apri', kind: 'apri' } : undefined,
        canSnooze: false, // le FYI si archiviano, non si rimandano
        canDone: true,    // done = segna come letta + archiveFyi
        actionData: { notifId: n.id },
      });
    }

    // ── Ordina, deduplica, applica i filtri locali ──
    const sorted = [...collected].sort(compareItems);
    // Dedupe per chiave entità: la prima occorrenza vince = banda più bassa (più urgente)
    const byKey = new Map<string, ActionItem>();
    for (const it of sorted) if (!byKey.has(it.key)) byKey.set(it.key, it);

    const archived = new Set(archivedFyi);
    const visible: ActionItem[] = [];
    let snoozedCount = 0;
    for (const it of byKey.values()) {
      // FYI archiviate: fuori per sempre (né visibili né contate)
      if (it.kind === 'notification' && archived.has(it.key)) continue;
      // Snooze attivo: nascosto ma contato; se la scadenza è passata, auto-scade
      const until = snoozedMap[it.key];
      if (until && new Date(until).getTime() > now) snoozedCount += 1;
      else visible.push(it);
    }

    const counts: ActionStreamCounts = {
      critical: visible.filter((i) => i.band === 0).length,
      today: visible.filter((i) => i.band === 1).length,
      week: visible.filter((i) => i.band === 2).length,
      later: visible.filter((i) => i.band === 3).length,
      snoozed: snoozedCount,
    };
    const bands: ActionBandGroup[] = ([0, 1, 2, 3] as const).map((band) => ({
      band,
      label: BAND_LABEL[band],
      items: visible.filter((i) => i.band === band),
    }));

    return { items: visible, counts, bands };
  }, [invoiceData, quoteData, notifData, overview, taskSummary, adminOverview, rateData, snoozedMap, archivedFyi, canFatturazione, canDashboard, canTalent, canTasks, canAdmin, canSeeMargin]);

  return { ...composed, isLoading };
}
